import { AuthUtils } from "@tecma/auth-utils";
import { logger } from "@tecma/logs-fe";
import { PayloadAction } from "@reduxjs/toolkit";
import Cookies from "js-cookie";
import { DateTime } from "luxon";
import { call, delay, put, select, takeLatest } from "redux-saga/effects";

import { DO_LOGOUT_COOKIE } from "constants/index";
import {
  EXPIRES_IN_RENT_COOKIE,
  JWT_RENT_COOKIE,
  PROJECT_ID_COOKIE_KEY,
  REFRESH_TOKEN_RENT_COOKIE,
} from "constants/auth";
import {
  setClientData,
  setLoadingClientData,
  setLoggedUser,
  setToken,
  unsetAuthenticated,
} from "store/slices/auth/slice";
import { environmentSelectors } from "store/slices/environment/selector";
import { EnvironmentState } from "store/slices/environment/slice";
import {
  ClientData,
  IToken,
  IUser,
  LoginByProjectData,
  User,
} from "types/auth";

import { applicationStateSelectors } from "store/slices/applicationState/selectors";
import { authStateSelectors } from "store/slices/auth/selectors";
import { authActions } from "./auth.actions";
import { applicationActions } from "../application/application.actions";
import { getClient } from "./auth.api";

function* doLogout() {
  yield put(unsetAuthenticated());
  AuthUtils.logout();
}

function* checkUserAuthentication() {
  const jwtCookie = Cookies.get(JWT_RENT_COOKIE);
  const projectId: string = yield select(applicationStateSelectors.projectID);
  const environment: EnvironmentState = yield select(
    environmentSelectors.environment,
  );

  if (jwtCookie && projectId) {
    yield put(
      setToken({
        accessToken: jwtCookie,
        expiresIn: Cookies.get(EXPIRES_IN_RENT_COOKIE) ?? "",
        refreshToken: Cookies.get(REFRESH_TOKEN_RENT_COOKIE) ?? "",
      }),
    );
    const userInfo: User = yield call(
      AuthUtils.getUserByJWT,
      projectId,
      environment.REACT_APP_AWS_TOKEN,
    );

    if (userInfo) {
      yield put(setLoggedUser(userInfo));
      const queryParams = new URLSearchParams(window.location.search);
      const isConfigurationComplete =
        queryParams.get("isConfigurationComplete") === "true";
      const colivingConfiguration = localStorage.getItem(
        "COLIVING_configuration",
      );
      if (colivingConfiguration && isConfigurationComplete) {
        const colivingConfigurationParsed = JSON.parse(colivingConfiguration);
        const { spaceId, checkIn, checkOut } =
          colivingConfigurationParsed[projectId];
        yield put(
          applicationActions.saveQuoteRequested({
            spaceId,
            checkIn,
            checkOut,
          }),
        );
      }
      const clientData: ClientData = yield getClient(
        projectId,
        userInfo.client,
      );

      if (userInfo) {
        yield put(setLoggedUser(userInfo));
        yield put(setClientData(clientData));
      } else {
        yield put(unsetAuthenticated());
      }
    }
  }
}
function* setPendingClientData() {
  yield put(setLoadingClientData(true));
}

function* setEndLoadingClientData() {
  yield put(setLoadingClientData(false));
}
function* refetchClientData() {
  yield put(authActions.setPendingClientData());
  const projectId: string = yield select(applicationStateSelectors.projectID);
  const clientId: string = yield select(authStateSelectors.clientID);
  const clientData: ClientData = yield getClient(projectId, clientId);

  yield put(setClientData(clientData));
  yield put(authActions.setEndLoadingClientData());
}

function* getNewToken(
  timeout: number,
  baseTimeout: number,
  token: IToken,
  user: IUser,
): unknown {
  const logout = Cookies.get(DO_LOGOUT_COOKIE);
  const environment: EnvironmentState = yield select(
    environmentSelectors.environment,
  );
  const { refreshToken: newRefreshToken, expiresIn } = token;
  try {
    if (
      logout !== "true" &&
      user &&
      baseTimeout >= 0 &&
      newRefreshToken &&
      expiresIn === Cookies.get(EXPIRES_IN_RENT_COOKIE)
    ) {
      const response = yield call(
        AuthUtils.refreshToken,
        newRefreshToken,
        user.email,
        environment.REACT_APP_AWS_TOKEN,
      );
      yield put(setToken(response));
    } else if (baseTimeout < 0 || logout === "true") {
      yield put(authActions.doLogout());
    }
  } catch (e) {
    logger.error(e);
    yield delay(timeout);
    yield getNewToken(timeout, baseTimeout, token, user);
  }
}

function* startCountDown(action: PayloadAction<LoginByProjectData>) {
  const { token, user } = action.payload;
  const environment: EnvironmentState = yield select(
    environmentSelectors.environment,
  );
  const targetTime =
    DateTime.fromISO(token.expiresIn).toMillis() -
    Number(environment.REACT_APP_TOKEN_EXPIRATION_THRESHOLD);
  const baseTimeout = targetTime - Date.now();
  const maxTimeoutValue = 2147483647;
  const timeout = baseTimeout > maxTimeoutValue ? maxTimeoutValue : baseTimeout;
  if (timeout > 0) {
    yield delay(timeout);
    if (Cookies.get(DO_LOGOUT_COOKIE) !== "true") {
      yield getNewToken(timeout, baseTimeout, token, user);
    }
  } else if (token?.accessToken && token?.expiresIn) {
    if (DateTime.fromISO(token.expiresIn) < DateTime.now()) {
      yield put(authActions.doLogout());
    }
  }
}

export default function* authSaga() {
  yield takeLatest(
    authActions.checkUserAuthentication,
    checkUserAuthentication,
  );
  yield takeLatest(authActions.doLogout, doLogout);
  yield takeLatest(authActions.startCountDown, startCountDown);
  yield takeLatest(authActions.refetchClientData, refetchClientData);
  yield takeLatest(authActions.setPendingClientData, setPendingClientData);
  yield takeLatest(
    authActions.setEndLoadingClientData,
    setEndLoadingClientData,
  );
}
